import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useBackgroundPollingStore from '@/store/useBackgroundPollingStore';
import type { BackgroundTask } from '@/store/useBackgroundPollingStore';
import { getCvAnalysisDetail } from '@/apis/cvAnalysis';
import { getCoverLetterAnalysisDetail } from '@/apis/coverLetterAnalysis';
import { getCLGenerationDetail } from '@/apis/coverLetterGeneration';
import { getInterviewResults } from '@/apis/interviewSimulation';

const POLL_INTERVAL = 5000; // 5s
const MAX_POLL_DURATION = 5 * 60 * 1000; // 5min

/**
 * Check if a background task has completed by calling the appropriate API.
 * Returns true if completed, false if still pending, throws on error.
 */
async function checkTaskCompleted(task: BackgroundTask): Promise<boolean> {
  switch (task.type) {
    case 'cv_analysis': {
      const res = await getCvAnalysisDetail(task.recordId as number);
      return res.data?.data?.status === 'completed';
    }
    case 'cl_analysis': {
      const res = await getCoverLetterAnalysisDetail(task.recordId as number);
      return res.data?.data?.status === 'completed';
    }
    case 'cl_generation': {
      const res = await getCLGenerationDetail(task.recordId as number);
      const data = res.data?.data;
      return data?.status === 'completed' && Boolean(data?.content);
    }
    case 'interview_analysis': {
      const res = await getInterviewResults(task.recordId as string);
      return res.data?.data?.status === 'completed';
    }
    default:
      return false;
  }
}

const BackgroundPollingManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const hasTasks = useBackgroundPollingStore((s) => s.tasks.length > 0);
  const removeTask = useBackgroundPollingStore((s) => s.removeTask);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = useRef(false);

  // Store latest refs to avoid stale closures in the interval callback
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const messageRef = useRef(message);
  messageRef.current = message;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!hasTasks) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const showReadyToast = (task: BackgroundTask) => {
      const key = `bg_ready_${task.id}`;
      messageRef.current.success({
        icon: <CheckCircleOutlined style={{ color: '#32D470' }} />,
        content: (
          <span
            onClick={() => {
              messageRef.current.destroy(key);
              navigateRef.current(task.navigateTo, {
                state: task.navigateState,
              });
            }}
            style={{ cursor: 'pointer' }}
          >
            {tRef.current('global.backgroundPolling.ready')}&nbsp;&nbsp;&gt;
          </span>
        ),
        key,
        duration: 8,
      });
    };

    const pollAll = async () => {
      // Guard: prevent concurrent executions
      if (isPollingRef.current) return;
      isPollingRef.current = true;

      try {
        const currentTasks = useBackgroundPollingStore.getState().tasks;
        for (const task of currentTasks) {
          // Timeout: silently remove
          if (Date.now() - task.createdAt > MAX_POLL_DURATION) {
            removeTask(task.id);
            continue;
          }

          try {
            const completed = await checkTaskCompleted(task);
            if (completed) {
              removeTask(task.id);
              showReadyToast(task);
            }
          } catch {
            // Transient API error: skip this task, retry on next cycle.
            // Timeout (MAX_POLL_DURATION) will handle permanent failures.
          }
        }
      } finally {
        isPollingRef.current = false;
      }
    };

    // Start interval polling (pollAll reads fresh state via getState)
    pollAll();
    timerRef.current = setInterval(pollAll, POLL_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasTasks, removeTask]);

  return null;
};

export default BackgroundPollingManager;
